/**
 * <product-gallery> - PDP hovedgalleri som vandret snap-carousel. Billederne
 * ligger i ét spor; pilene bladrer ét slide ad gangen. _narrow/_wide-shots
 * ligger aldrig i sporet, men i understregionen, som ikke roeres her.
 *
 * Progressive enhancement: uden JS er sporet et almindeligt scroll-snap-spor
 * man swiper/scroller i, og pilene er skjult (hidden). Med JS afsloeres pilene,
 * de scroller sporet, og de deaktiveres i hver ende (ingen loop). Taelleren pr.
 * slide opdaterer sig selv via snap - ingen JS-tekst.
 *
 * Variant-skift (variant-picker) scroller sporet til variantens featured-media
 * vandret paa sporet (aldrig siden); scroll-lytteren her opdaterer pilenes ende-tilstand med.
 *
 * Elementet reconnecter selv ved shopify:section:load, saa editoren
 * re-initialiserer uden fejl; lyttere ryddes op i disconnectedCallback.
 *
 * PERFORMANCE (2026-09-29). Tre ting gjorde swipet hakkende, alle maalt paa
 * /products/wool-duffel-coat-night-sky (8 medier) ved 1440:
 *
 * 1. Billede 2..n er `loading="lazy"` i markup'en. Browseren henter kun lazy-
 *    billeder inden for ~1250px af viewporten - ogsaa VANDRET - saa i et spor
 *    med ~890px brede slides begyndte slide 3 foerst at hente naar man allerede
 *    swipede. Maalt ventetid foer billedet stod der: 2468ms paa slide 3, 563ms
 *    paa slide 4. Derfor varmer elementet selv billederne: naboerne (to frem,
 *    ét tilbage) paa idle efter `load`, RESTEN foerst naar kunden roerer
 *    galleriet.
 *    Det KOSTER ikke bytes paa et cold load, og det er maalt, ikke antaget:
 *    1440 med 8 medier ligger paa 307KB billeder med varmningen og 311KB uden
 *    (samme 8 requests) - browserens egen lazy-taerskel hentede allerede de
 *    naermeste slides, bare for sent og udekodet. 375: 155KB, 3 galleribilleder.
 *    Intent-varmningen af RESTEN er derimod rigtige ekstra bytes, og derfor
 *    haenger den paa en faktisk handling (se onIntent) i stedet for paa hover.
 *    De 307KB er i oevrigt over budgettets 300KB baade foer og efter: det er
 *    kildefilernes vaegt, ikke galleriets kode. Se BACKLOG KATALOG-BILLEDVAEGT.
 * 2. Ingen `decoding` nogen steder: et 1400px JPEG blev dekodet paa main thread
 *    praecis mens snappet animerede. Markup'en saetter nu decoding="async" paa
 *    alt undtagen LCP-billedet, og varmningen af NABOERNE kalder `decode()`, saa
 *    bitmappen ER klar foer slidet scroller ind. Intent-varmningen af resten
 *    henter kun - N samtidige dekodninger ville vaere det modsatte af hensigten.
 * 3. #sync() laeste scrollWidth/clientWidth OG skrev disabled/tekst/klasser i
 *    hver eneste frame under scroll = forced synchronous layout pr. frame.
 *    Maalene caches nu i en ResizeObserver, og der skrives kun naar en vaerdi
 *    faktisk aendrer sig. Maalt efter: median frame 16,7ms, p95 18,6ms, nul
 *    frames over 32ms og nul long tasks under en fuld gennembladring.
 *
 * Dertil bladrer #step til slidets faktiske kant i stedet for `scrollBy(
 * clientWidth)`: ved broekdels-kolonnebredder (892,33px) ramte den ikke kanten,
 * og mandatory snap rettede bagefter - to animationer oven i hinanden.
 */

// Hvor laenge et bladre-maal er gyldigt. Se #step.
const PENDING_TTL = 800;

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    this.counter = this.querySelector('[data-gallery-count]');
    this.dots = [...this.querySelectorAll('[data-gallery-dots] .product-gallery__dot')];
    if (!this.track || !this.prev || !this.next) return;

    // Under 2 slides er der intet at bladre i - lad pilene blive skjult.
    this.slides = [...this.track.querySelectorAll('.product-gallery__slide')];
    if (this.slides.length < 2) return;

    // Markér som JS-forbedret: foerst her skjules scrollbaren (uden JS er den
    // musens affordance), og pilene afsloeres som primaer navigation.
    this.classList.add('product-gallery--enhanced');
    this.prev.hidden = false;
    this.next.hidden = false;

    this.index = 0;
    // Server-renderet aktiv prik; #syncDots flytter den herfra.
    this.activeDot = 0;
    this.pending = null;
    this.pendingAt = 0;
    this.warmedAll = false;

    this.onPrev = () => this.#step(-1);
    this.onNext = () => this.#step(1);
    this.onScroll = this.#raf(() => this.#sync());
    this.onKey = (event) => this.#key(event);
    // Foerste beroering af galleriet = kunden er paa vej ind i det. Hent
    // resten nu, mens fingeren stadig er paa vej. passive: scroll/touch maa
    // ikke vente paa os. IKKE `pointerenter`: galleriet er hele venstre
    // kolonne paa desktop, saa musen krydser det paa vej til koebsboksen, og
    // hver eneste besoegende med mus ville hente hele galleriet (maalt: 212 ->
    // ~360KB paa et 8-medie-produkt, altsaa forbi budgettets 300KB). Et
    // pointerdown er derimod en faktisk handling.
    this.onIntent = () => this.#warmAll();
    this.intentEvents = ['pointerdown', 'touchstart', 'keydown', 'focusin'];

    this.prev.addEventListener('click', this.onPrev);
    this.next.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('keydown', this.onKey);
    this.intentEvents.forEach((type) => this.addEventListener(type, this.onIntent, { passive: true }));

    // Maalene laeses KUN her og ved resize - aldrig i scroll-lytteren.
    // ResizeObserver daekker ogsaa 834px-skiftet, hvor sporet skifter bredde.
    this.resizeObserver = new ResizeObserver(() => {
      this.#measure();
      this.#sync(true);
    });
    this.resizeObserver.observe(this.track);

    this.#measure();
    this.#sync(true);
    this.#warmOnIdle();
  }

  // Cachede maal + det saet slides der faktisk vises. offsetParent === null
  // udelader et slide der er display: none. Ingen slides i sporet skjules i dag
  // (_narrow/_wide ligger uden for det), saa filteret er en ren vagt.
  #measure() {
    this.visible = this.slides.filter((slide) => slide.offsetParent !== null);
    this.width = this.track.clientWidth || 1;
    this.max = this.track.scrollWidth - this.track.clientWidth;
  }

  #step(dir) {
    const total = this.visible.length;
    if (!total) return;

    // `pending` er det slide en smooth scroll er paa vej mod. Uden den ville
    // to hurtige klik paa pilen begge sigte mod det samme slide. MEN den maa
    // ikke overleve sin egen animation: bliver den programmatiske scroll
    // afbrudt - kunden swiper selv, eller variant-picker scroller sporet et
    // helt andet sted hen - saa er maalet forkert, og uden en udloebstid ville
    // pilen derefter springe slides over og til sidst staa doed i enden (den
    // saa aktiv ud, men `to === from` og den gjorde intet). Et maal gaelder
    // derfor kun mens animationen kan vaere i luften.
    const fresh = this.pending !== null && performance.now() - this.pendingAt < PENDING_TTL;
    const from = fresh ? this.pending : this.index;
    const to = Math.min(total - 1, Math.max(0, from + dir));
    if (to === from) return;
    const target = this.visible[to];
    if (!target) return;
    this.pending = to;
    this.pendingAt = performance.now();

    // Bladr til slidets FAKTISKE kant. Rects laeses paa den aktuelle (evt.
    // animerede) position, saa deltaet er praecis det der er tilbage - ogsaa
    // midt i en igangvaerende smooth scroll.
    const delta = target.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: delta, behavior: reduce ? 'auto' : 'smooth' });
    this.#warmAround(to);
  }

  // Venstre/hoejre-pil bladrer, men kun naar fokus staar paa en af pilene
  // (ellers ikke stjaele taster fra resten af siden).
  #key(event) {
    if (event.target !== this.prev && event.target !== this.next) return;
    if (event.key === 'ArrowLeft') {
      this.#step(-1);
    } else if (event.key === 'ArrowRight') {
      this.#step(1);
    } else {
      return;
    }
    event.preventDefault();
  }

  // Koeres pr. frame under scroll: laeser KUN scrollLeft (maalene er cachede)
  // og skriver kun naar en vaerdi faktisk har aendret sig.
  #sync(force) {
    const total = this.visible.length;
    if (!total) return;
    const x = this.track.scrollLeft;
    const atStart = x <= 1;
    const atEnd = x >= this.max - 1;
    // Flyt fokus til den modsatte (stadig aktive) pil FOER vi deaktiverer den
    // fokuserede - ellers falder fokus til <body> og hopper til sidens top.
    if (atStart && document.activeElement === this.prev) this.next.focus();
    else if (atEnd && document.activeElement === this.next) this.prev.focus();
    if (this.prev.disabled !== atStart) this.prev.disabled = atStart;
    if (this.next.disabled !== atEnd) this.next.disabled = atEnd;

    const current = Math.min(total - 1, Math.max(0, Math.round(x / this.width)));
    if (!force && current === this.index) return;
    this.index = current;
    if (this.pending === current) this.pending = null;

    // Opdater taeller og dots ud fra hvilket slide der er i fokus. Dots'ene
    // opdateres ogsaa uden taeller (den er slaaet fra i markup'en).
    if (this.counter) this.counter.textContent = `${current + 1}/${total}`;
    this.#syncDots(current);
    this.#warmAround(current);
  }

  // Dots (mobil, design ui_kits/pdp-mobile): samme position som taelleren.
  // Rent dekorative - taelleren er den tilgaengelige positionsangivelse - saa
  // her skiftes kun en klasse, ingen aria-tilstand. Kun de to prikker der
  // skifter roeres; en loekke over alle i hver frame var en af hakkene.
  #syncDots(current) {
    if (!this.dots.length) return;
    const previous = this.dots[this.activeDot];
    if (previous) previous.classList.remove('product-gallery__dot--active');
    const active = this.dots[current];
    if (active) active.classList.add('product-gallery__dot--active');
    this.activeDot = current;
  }

  // Save-Data eller en maalt 2g-forbindelse: hent ikke noget kunden ikke har
  // bedt om. Gaelder BAADE idle- og intent-varmningen - ellers ville den vej
  // der henter mest vaere den eneste uden gate.
  #frugal() {
    const connection = navigator.connection;
    if (!connection) return false;
    return Boolean(connection.saveData) || /(^|-)2g$/.test(connection.effectiveType || '');
  }

  // Hent billedet nu. `loading = 'eager'` paa et lazy-billede der endnu ikke
  // er startet saetter hentningen i gang med det samme; low fetchpriority
  // holder det ude af koe med LCP-billedet (saettes FOER flippet, saa
  // prioriteten gaelder fra foerste byte). decode() flytter dekodningen vaek
  // fra den frame hvor slidet snapper ind - men kun hvor vi beder om det.
  #warm(image, decode) {
    if (!image || image.dataset.warmed) return;
    image.dataset.warmed = '1';
    if (image.loading === 'lazy') {
      image.setAttribute('fetchpriority', 'low');
      image.loading = 'eager';
    }
    if (decode && image.decode) image.decode().catch(() => {});
  }

  // To slides FREM, ét tilbage, og dekodet. To frem fordi ét ikke raekker:
  // maalt paa 375 ventede slide 3 stadig 1185ms naar kunden swipede hurtigere
  // end intent-hentningen naaede frem. Med to frem: 0ms paa samtlige trin.
  #warmAround(i) {
    const window = this.#frugal() ? [i, i + 1] : [i, i + 1, i + 2, i - 1];
    window.forEach((n) => {
      const slide = this.visible[n];
      if (slide) this.#warm(slide.querySelector('img'), true);
    });
  }

  // Resten af galleriet, hentet men IKKE dekodet: paa et 8-10-slides produkt
  // ville N samtidige dekodninger (~10MB RGBA pr. billede) lande praecis i det
  // oejeblik fingeren rammer. #warmAround dekoder vinduet omkring kunden.
  #warmAll() {
    if (this.warmedAll || this.#frugal()) return;
    this.warmedAll = true;
    this.visible.forEach((slide) => this.#warm(slide.querySelector('img'), false));
  }

  // Naboerne hentes paa idle efter `load`, saa foerste swipe aldrig venter.
  // Handlet gemmes: variant-picker re-renderer hele sektionen ved hvert
  // variant-klik, saa callbacken kan ellers fyre op til 2000ms efter at
  // elementet er skiftet ud og varme billeder i et frakoblet DOM-traer.
  #warmOnIdle() {
    if (this.#frugal()) return;
    const run = () => this.#warmAround(this.index);
    this.warmIdle = () => {
      if (window.requestIdleCallback) this.idleHandle = window.requestIdleCallback(run, { timeout: 2000 });
      else this.idleTimer = setTimeout(run, 200);
    };
    if (document.readyState === 'complete') this.warmIdle();
    else window.addEventListener('load', this.warmIdle, { once: true });
  }

  // Saml scroll-events til én pr. frame.
  #raf(fn) {
    return () => {
      if (this.rafHandle) return;
      this.rafHandle = requestAnimationFrame(() => {
        this.rafHandle = 0;
        fn();
      });
    };
  }

  disconnectedCallback() {
    if (this.prev) this.prev.removeEventListener('click', this.onPrev);
    if (this.next) this.next.removeEventListener('click', this.onNext);
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('keydown', this.onKey);
    if (this.intentEvents) this.intentEvents.forEach((type) => this.removeEventListener(type, this.onIntent));
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.warmIdle) window.removeEventListener('load', this.warmIdle);
    if (this.idleHandle && window.cancelIdleCallback) window.cancelIdleCallback(this.idleHandle);
    if (this.idleTimer) clearTimeout(this.idleTimer);
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
    this.classList.remove('product-gallery--enhanced');
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
